import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SchemaComposer } from "../components/design/SchemaComposer";
import { SchemaReviewForm } from "../components/design/SchemaReviewForm";
import { useInterviewRunner } from "../state/useInterviewRunner";
import type { InterviewTemplate } from "../templates/types";
import type { DesignResponse } from "../api/client";

type Phase = "compose" | "review" | "starting";

/**
 * Design Your Own (§12): compose -> review -> run, with no path that skips
 * the review step. On approval this dispatches TEMPLATE_LOADED into the
 * SAME InterviewProvider/reducer the default check-in uses — there is no
 * second engine and no second turn endpoint for a generated template.
 *
 * Why this component (and not SchemaReviewForm) calls startInterview():
 * TEMPLATE_LOADED resets interview state and moves phase straight to
 * "running" (see engine/reducer.ts), so there is no "idle"-phase
 * "Start check-in" button for the user to press after approval — without
 * this component bootstrapping the freshly loaded template itself, the
 * approved interview would render with no continuity token and every
 * message would silently go nowhere. This still goes through the identical
 * useInterviewRunner.startInterview()/POST /api/interview/start path
 * RunCheckIn's own "Start check-in" button uses — no new request shape, no
 * digest computed here (§7) — it is only sequenced to wait for the
 * dispatched template to land before calling it, since dispatch does not
 * update state synchronously.
 */
export function DesignYourOwn() {
  const [phase, setPhase] = useState<Phase>("compose");
  const [generated, setGenerated] = useState<DesignResponse | null>(null);
  const { state, dispatch, startInterview } = useInterviewRunner();
  const navigate = useNavigate();
  const pendingTemplateId = useRef<string | null>(null);

  useEffect(() => {
    if (phase !== "starting" || !pendingTemplateId.current) return;
    if (state.templateId !== pendingTemplateId.current) return;

    if (!state.continuityToken) {
      startInterview();
      return;
    }

    navigate("/run");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, state.templateId, state.continuityToken]);

  function handleApprove(template: InterviewTemplate) {
    pendingTemplateId.current = template.id;
    dispatch({ type: "TEMPLATE_LOADED", template, locale: "en" });
    setPhase("starting");
  }

  if (phase === "compose") {
    return <SchemaComposer onGenerated={(result) => { setGenerated(result); setPhase("review"); }} />;
  }

  if (phase === "review" && generated) {
    return <SchemaReviewForm template={generated.template} notice={generated.notice} onApprove={handleApprove} />;
  }

  return (
    <div role="status" aria-live="polite" className="flex h-full items-center justify-center p-8 text-sm text-ink-soft">
      Starting your check-in…
    </div>
  );
}
